import { useState } from 'react';

export default function CTASection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    propertyCondition: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5u5tl1dkj8fk0537p5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          propertyCondition: '',
          timeline: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact-form" className="py-20 bg-gradient-to-br from-gray-900 to-gray-800">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to <span className="text-teal-400">Get Started?</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Take the first step toward selling your hoarder house. Fill out the form below or give us a call - we're here to help.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div className="text-white">
            <h3 className="text-2xl font-bold mb-6">What Happens Next?</h3>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-xl font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-bold mb-2">We'll Contact You</h4>
                  <p className="text-gray-300 text-sm">Within 24 hours, we'll reach out to discuss your situation and answer any questions you have. No pressure, just helpful information.</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-xl font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-bold mb-2">Schedule a Walkthrough</h4>
                  <p className="text-gray-300 text-sm">We'll arrange a convenient time to view the property. This is a quick, judgment-free assessment - we don't need access to every room.</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-xl font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-bold mb-2">Receive Your Cash Offer</h4>
                  <p className="text-gray-300 text-sm">Within 24-48 hours of viewing the property, we'll present you with a fair, no-obligation cash offer.</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-xl font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-bold mb-2">Close on Your Timeline</h4>
                  <p className="text-gray-300 text-sm">If you accept, we can close in as little as 7 days or wait longer if you need more time. You choose the timeline that works for you.</p>
                </div>
              </div>
            </div>

            <div className="mt-10 p-6 bg-teal-600 rounded-xl">
              <h4 className="font-bold text-xl mb-4">Prefer to Talk?</h4>
              <p className="text-teal-50 mb-4">We understand that some situations are easier to discuss over the phone. Give us a call anytime.</p>
              <a 
                href="tel:+1-915-206-5814"
                className="inline-flex items-center px-6 py-3 bg-white text-teal-600 font-bold rounded-lg hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl mr-2"></i>
                (915) 206-5814
              </a>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <form id="hoarder-house-inquiry-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="(123) 456-7890"
                />
              </div>

              <div>
                <label htmlFor="propertyAddress" className="block text-sm font-semibold text-gray-900 mb-2">
                  Property Address *
                </label>
                <input
                  type="text"
                  id="propertyAddress"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="123 Main St, City, State ZIP"
                />
              </div>

              <div>
                <label htmlFor="propertyCondition" className="block text-sm font-semibold text-gray-900 mb-2">
                  Property Condition
                </label>
                <select
                  id="propertyCondition"
                  name="propertyCondition"
                  value={formData.propertyCondition}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">Select condition...</option>
                  <option value="moderate-clutter">Moderate clutter</option>
                  <option value="severe-clutter">Severe clutter</option>
                  <option value="non-livable">Non-livable condition</option>
                  <option value="health-hazards">Health/safety hazards present</option>
                  <option value="not-sure">Not sure / Prefer to discuss</option>
                </select>
              </div>

              <div>
                <label htmlFor="timeline" className="block text-sm font-semibold text-gray-900 mb-2">
                  When do you need to sell?
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">Select timeline...</option>
                  <option value="asap">As soon as possible</option>
                  <option value="1-month">Within 1 month</option>
                  <option value="1-3-months">1-3 months</option>
                  <option value="3-6-months">3-6 months</option>
                  <option value="flexible">Flexible / Just exploring options</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                  Tell us about your situation (optional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="Any additional details you'd like to share..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-teal-600 text-white text-lg font-semibold rounded-lg hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Get My Cash Offer'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 text-sm font-semibold">
                    <i className="ri-checkbox-circle-line mr-2"></i>
                    Thank you! We'll contact you within 24 hours.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm font-semibold">
                    <i className="ri-error-warning-line mr-2"></i>
                    Something went wrong. Please try again or call us directly.
                  </p>
                </div>
              )}

              <p className="text-xs text-gray-600 text-center">
                Your information is kept completely confidential. We respect your privacy and will never share your details with third parties.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
